/**
 * /project/$projectId — project home (Boards / Members / Settings tabs).
 *
 * Notion-style chrome: single-row header with title + tab switcher, no
 * coloured chrome, no breadcrumbs (the sidebar handles navigation).
 *
 * Tabs:
 *   - Boards  → grid of project boards + "+ New board" inline form.
 *   - Members → table of project_members + add-from-workspace picker.
 *   - Settings → rename, recolor, archive.
 */
import type { Board } from '@chaos/types/kanban';
import type { Project, ProjectMemberRole } from '@chaos/types/project';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Loader2, Plus, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { workspaceMemberKeys, workspaceMembersApi } from '../../../lib/api/invitations';
import { kanbanApi, kanbanKeys } from '../../../lib/api/kanban';
import { projectKeys, projectsApi } from '../../../lib/api/projects';
import {
  colorTint,
  normaliseColor,
  STATUS_COLORS,
  type StatusColor,
} from '../../../lib/status-color';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const TAB_VALUES = ['boards', 'members', 'settings'] as const;
type TabValue = (typeof TAB_VALUES)[number];

function ProjectPage() {
  const { t } = useTranslation();
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const search = useSearch({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const activeRole = useAuthStore(
    (s) => s.workspaces.find((w) => w.workspace.id === s.activeWorkspaceId)?.membership.role,
  );
  const isAdmin = activeRole === 'founder' || activeRole === 'admin';

  const activeTab: TabValue = (TAB_VALUES as readonly string[]).includes(search.tab ?? '')
    ? (search.tab as TabValue)
    : 'boards';

  const projectQ = useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: () => projectsApi.get(projectId),
  });

  const tint = projectQ.data ? colorTint(normaliseColor(projectQ.data.color)) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-4 px-6 pt-3 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          {tint && (
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: tint.foreground }}
            />
          )}
          <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
            {projectQ.data?.name ?? t('common.loading')}
          </h1>
        </div>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if ((TAB_VALUES as readonly string[]).includes(value)) {
            void navigate({
              to: '/project/$projectId',
              params: { projectId },
              search: { tab: value as TabValue },
              replace: true,
            });
          }
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="mx-6 mt-1 mb-3 inline-flex w-fit gap-1">
          <TabsTrigger value="boards">{t('projects.tab_boards')}</TabsTrigger>
          <TabsTrigger value="members">{t('projects.tab_members')}</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings">{t('projects.tab_settings')}</TabsTrigger>}
        </TabsList>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <TabsContent value="boards" className="mt-0">
            <ProjectBoardsTab projectId={projectId} />
          </TabsContent>
          <TabsContent value="members" className="mt-0">
            <ProjectMembersTab projectId={projectId} isAdmin={isAdmin} />
          </TabsContent>
          {isAdmin && projectQ.data && (
            <TabsContent value="settings" className="mt-0">
              <ProjectSettingsTab project={projectQ.data} />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
}

interface ProjectBoardsTabProps {
  projectId: string;
}

function ProjectBoardsTab({ projectId }: ProjectBoardsTabProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const activeRole = useAuthStore(
    (s) => s.workspaces.find((w) => w.workspace.id === s.activeWorkspaceId)?.membership.role,
  );
  const canCreateBoards = activeRole !== 'auditor' && activeRole !== 'external';

  const boardsQ = useQuery({
    queryKey: kanbanKeys.boards(),
    queryFn: () => kanbanApi.listBoards(),
  });

  const createM = useMutation({
    mutationFn: (name: string) =>
      kanbanApi.createBoard({ name, projectId, template: 'kanban-default' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kanbanKeys.boards() }),
  });

  const [creating, setCreating] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setCreating(false);
      return;
    }
    await createM.mutateAsync(trimmed);
    setDraft('');
    setCreating(false);
  };

  const filtered = (boardsQ.data ?? []).filter((b: Board) => b.projectId === projectId);

  if (boardsQ.isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }
  if (filtered.length === 0 && !creating && !canCreateBoards) {
    return <p className="text-sm text-muted-foreground">{t('projects.no_boards')}</p>;
  }

  return (
    <div>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((b: Board) => (
          <li key={b.id}>
            <Link
              to="/board/$boardId"
              params={{ boardId: b.id }}
              className="block rounded border border-border bg-background px-4 py-3 text-sm transition-colors hover:bg-foreground/5"
            >
              <span className="block truncate text-[13px] font-medium text-foreground">
                {b.name}
              </span>
              {b.description && (
                <span className="mt-1 line-clamp-2 block text-[12px] text-muted-foreground">
                  {b.description}
                </span>
              )}
            </Link>
          </li>
        ))}
        {canCreateBoards && (
          <li>
            {creating ? (
              <div className="rounded border border-border bg-background px-3 py-2">
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => {
                    if (!draft.trim()) setCreating(false);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      await submit();
                    }
                    if (e.key === 'Escape') {
                      setDraft('');
                      setCreating(false);
                    }
                  }}
                  placeholder={t('kanban.boards.placeholder')}
                  aria-label={t('kanban.boards.placeholder')}
                  className="w-full rounded bg-muted/30 px-2 py-1 text-[13px] outline-none ring-1 ring-border focus-visible:ring-foreground/40"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center justify-center gap-1.5 rounded border border-dashed border-border px-4 py-3 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                <span>{t('projects.create_board')}</span>
              </button>
            )}
          </li>
        )}
      </ul>
    </div>
  );
}

interface ProjectMembersTabProps {
  projectId: string;
  isAdmin: boolean;
}

const ROLE_LABEL_KEYS: Record<ProjectMemberRole, string> = {
  owner: 'projects.role_owner',
  editor: 'projects.role_editor',
  commenter: 'projects.role_commenter',
  viewer: 'projects.role_viewer',
};

function ProjectMembersTab({ projectId, isAdmin }: ProjectMembersTabProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const membersQ = useQuery({
    queryKey: projectKeys.members(projectId),
    queryFn: () => projectsApi.listMembers(projectId),
  });
  const workspaceMembersQ = useQuery({
    queryKey: activeWorkspaceId ? workspaceMemberKeys.list(activeWorkspaceId) : ['noop'],
    queryFn: () =>
      activeWorkspaceId ? workspaceMembersApi.list(activeWorkspaceId) : Promise.resolve([]),
    enabled: !!activeWorkspaceId,
  });

  const addM = useMutation({
    mutationFn: (params: { userId: string; role: ProjectMemberRole }) =>
      projectsApi.addMember(projectId, params),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.members(projectId) }),
  });
  const removeM = useMutation({
    mutationFn: (userId: string) => projectsApi.removeMember(projectId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.members(projectId) }),
  });

  const existingIds = new Set((membersQ.data ?? []).map((m) => m.userId));
  const eligible = (workspaceMembersQ.data ?? []).filter((u) => !existingIds.has(u.userId));

  const [selectedUserId, setSelectedUserId] = React.useState('');
  const [selectedRole, setSelectedRole] = React.useState<ProjectMemberRole>('editor');

  return (
    <div className="space-y-4">
      <p className="text-[12px] text-muted-foreground">{t('projects.members_subtitle')}</p>

      <ul className="divide-y divide-border rounded border border-border">
        {membersQ.isLoading && (
          <li className="px-4 py-3 text-[13px] text-muted-foreground">{t('common.loading')}</li>
        )}
        {membersQ.data?.length === 0 && (
          <li className="px-4 py-3 text-[13px] text-muted-foreground">
            {t('projects.no_members')}
          </li>
        )}
        {membersQ.data?.map((m) => (
          <li key={m.id} className="group/row flex items-center gap-3 px-4 py-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[13px] font-medium text-foreground">
                {m.displayName ?? m.email}
              </span>
              <span className="truncate text-[11px] text-muted-foreground">{m.email}</span>
            </div>
            <span className="rounded bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-foreground">
              {t(ROLE_LABEL_KEYS[m.role as ProjectMemberRole] ?? 'projects.role_viewer')}
            </span>
            {isAdmin && m.role !== 'owner' && (
              <button
                type="button"
                onClick={async () => {
                  if (window.confirm(`Remove ${m.email}?`)) {
                    await removeM.mutateAsync(m.userId);
                  }
                }}
                aria-label={t('projects.remove_member')}
                className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/5 hover:text-error group-hover/row:opacity-100"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          {eligible.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">{t('projects.no_eligible_users')}</p>
          ) : (
            <>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1 text-[13px]"
                aria-label={t('projects.select_user')}
              >
                <option value="">{t('projects.select_user')}</option>
                {eligible.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.displayName ?? u.email}
                  </option>
                ))}
              </select>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as ProjectMemberRole)}
                className="rounded border border-border bg-background px-2 py-1 text-[13px]"
                aria-label={t('projects.select_role')}
              >
                <option value="owner">{t('projects.role_owner')}</option>
                <option value="editor">{t('projects.role_editor')}</option>
                <option value="commenter">{t('projects.role_commenter')}</option>
                <option value="viewer">{t('projects.role_viewer')}</option>
              </select>
              <Button
                variant="secondary"
                size="sm"
                disabled={!selectedUserId || addM.isPending}
                onClick={async () => {
                  if (!selectedUserId) return;
                  await addM.mutateAsync({ userId: selectedUserId, role: selectedRole });
                  setSelectedUserId('');
                }}
              >
                {addM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                {t('projects.add_member')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface ProjectSettingsTabProps {
  project: Project;
}

function ProjectSettingsTab({ project }: ProjectSettingsTabProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = React.useState(project.name);
  const [description, setDescription] = React.useState(project.description ?? '');
  const [color, setColor] = React.useState<StatusColor>(normaliseColor(project.color));

  React.useEffect(() => {
    setName(project.name);
    setDescription(project.description ?? '');
    setColor(normaliseColor(project.color));
  }, [project]);

  const updateM = useMutation({
    mutationFn: () =>
      projectsApi.update(project.id, {
        name,
        description: description.length > 0 ? description : null,
        color,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: projectKeys.list() });
      await qc.invalidateQueries({ queryKey: projectKeys.detail(project.id) });
    },
  });

  const archiveM = useMutation({
    mutationFn: () => projectsApi.archive(project.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: projectKeys.list() });
      await navigate({ to: '/boards' });
    },
  });

  const dirty =
    name !== project.name ||
    description !== (project.description ?? '') ||
    color !== normaliseColor(project.color);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!dirty) return;
        await updateM.mutateAsync();
      }}
      className="max-w-xl space-y-4"
    >
      <div>
        <label
          htmlFor="project-name"
          className="text-[11px] uppercase tracking-wider text-muted-foreground"
        >
          {t('projects.settings_name')}
        </label>
        <input
          id="project-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-[13px] outline-none focus-visible:ring-1 focus-visible:ring-foreground/40"
        />
      </div>

      <div>
        <label
          htmlFor="project-description"
          className="text-[11px] uppercase tracking-wider text-muted-foreground"
        >
          {t('projects.settings_description')}
        </label>
        <textarea
          id="project-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-[13px] outline-none focus-visible:ring-1 focus-visible:ring-foreground/40"
        />
      </div>

      <fieldset className="border-0 p-0">
        <legend className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {t('projects.settings_color')}
        </legend>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {STATUS_COLORS.map((c) => {
            const tint = colorTint(c);
            const isActive = color === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={c}
                aria-pressed={isActive}
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full border',
                  isActive ? 'border-foreground/60' : 'border-border',
                )}
                style={{ backgroundColor: tint.background, color: tint.foreground }}
              >
                <span
                  aria-hidden
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: tint.foreground }}
                />
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!dirty || updateM.isPending}>
          {updateM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {t('projects.settings_save')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-error"
          onClick={async () => {
            if (window.confirm(`Archive “${project.name}”?`)) {
              await archiveM.mutateAsync();
            }
          }}
        >
          {t('projects.settings_archive')}
        </Button>
      </div>
    </form>
  );
}

export const Route = createFileRoute('/_authenticated/project/$projectId')({
  component: ProjectPage,
});
